import { authClient } from "@/lib/auth-client";
import { formatDateOnly, parseDateOnly } from "@/lib/date-only";
import { openDocFile } from "@/lib/open-doc-file";
import { useState, useRef, useEffect, useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { folderPage } from "@/lib/folder-pages";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  FolderOpen,
  Folder,
  ArrowLeft,
  FileText,
  Download,
  Eye,
  ChevronRight,
  Globe,
  Users,
  Building2,
  EyeOff,
  FileWarning,
  Loader2,
  ClipboardList,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RotateCcw,
  PenLine,
  Trash2,
  Calendar,
  User,
  BookOpen,
  ChevronLeft,
} from "lucide-react";
import { apiGet, apiPost } from "@/lib/api";
import { MessageText } from "@/components/ui/message-text";

export const Route = createFileRoute("/_authenticated/documents")({
  validateSearch: (s: Record<string, unknown>): { tab?: "assignments" | "library" } =>
    s.tab === "assignments" || s.tab === "library" ? { tab: s.tab } : {},
  component: DocumentsPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface DocCategory {
  id: string;
  name: string;
  description: string | null;
  scope: "company" | "division";
  parentId?: string | null;
}

interface SafetyDoc {
  id: string;
  title: string;
  description: string | null;
  categoryId: string;
  visibility: "everyone" | "divisions" | "users" | "none";
  currentVersionId: string | null;
  status: string;
  updatedAt: string;
}

interface MyAssignment {
  ackId: string;
  assignmentId: string;
  doc: { id: string; title: string; description: string | null } | null;
  assigner: { id: string; name: string } | null;
  dueDate: string | null;
  message: string | null;
  docStatus: string;
  signedAt: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  userNote: string | null;
  adminNote: string | null;
  completionStatus: "approved" | "awaiting_approval" | "signed" | "incomplete";
  createdAt: string;
}

interface QuizQuestion {
  id: string;
  questionText: string;
  optionA: string;
  optionB: string;
  optionC: string | null;
  optionD: string | null;
}

interface QuizData {
  quizId: string;
  passScore: number;
  questionCount: number;
  questions: QuizQuestion[];
  previousAttempts: number;
  lastScore: number | null;
  alreadyPassed: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function VisibilityBadge({ visibility }: { visibility: string }) {
  const map: Record<string, { label: string; icon: React.ReactNode; cls: string }> = {
    everyone: { label: "Company-wide", icon: <Globe className="h-3 w-3" />, cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
    divisions: { label: "Division", icon: <Building2 className="h-3 w-3" />, cls: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
    users: { label: "Assigned", icon: <Users className="h-3 w-3" />, cls: "bg-violet-500/15 text-violet-700 dark:text-violet-400" },
    none: { label: "Restricted", icon: <EyeOff className="h-3 w-3" />, cls: "bg-muted text-muted-foreground" },
  };
  const v = map[visibility] ?? map.none;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${v.cls}`}>
      {v.icon}{v.label}
    </span>
  );
}

function StatusBadge({ status }: { status: MyAssignment["completionStatus"] }) {
  const map = {
    approved: { label: "Completed", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
    awaiting_approval: { label: "Submitted", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
    signed: { label: "Signed", cls: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
    incomplete: { label: "Pending", cls: "bg-muted text-muted-foreground" },
  };
  const s = map[status];
  return (
    <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full ${s.cls}`}>
      {s.label}
    </span>
  );
}

// ── Signature Pad ─────────────────────────────────────────────────────────────

function SignaturePad({ onCapture, onClear }: {
  onCapture: (dataUrl: string | null) => void;
  onClear: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  function getPos(e: MouseEvent | TouchEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const src = "touches" in e ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    function start(e: MouseEvent | TouchEvent) {
      e.preventDefault();
      drawing.current = true;
      const { x, y } = getPos(e, canvas!);
      ctx.beginPath();
      ctx.moveTo(x, y);
    }
    function move(e: MouseEvent | TouchEvent) {
      if (!drawing.current) return;
      e.preventDefault();
      const { x, y } = getPos(e, canvas!);
      ctx.lineTo(x, y);
      ctx.stroke();
      hasDrawn.current = true;
    }
    function end() {
      if (!drawing.current) return;
      drawing.current = false;
      if (hasDrawn.current) {
        onCapture(canvas!.toDataURL("image/png"));
      }
    }

    canvas.addEventListener("mousedown", start);
    canvas.addEventListener("mousemove", move);
    canvas.addEventListener("mouseup", end);
    canvas.addEventListener("mouseleave", end);
    canvas.addEventListener("touchstart", start, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", end);

    return () => {
      canvas.removeEventListener("mousedown", start);
      canvas.removeEventListener("mousemove", move);
      canvas.removeEventListener("mouseup", end);
      canvas.removeEventListener("mouseleave", end);
      canvas.removeEventListener("touchstart", start);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", end);
    };
  }, [onCapture]);

  function clear() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasDrawn.current = false;
    onCapture(null);
    onClear();
  }

  return (
    <div className="space-y-1">
      <div className="relative border-2 border-dashed border-border rounded-lg overflow-hidden bg-white dark:bg-white/5">
        <canvas
          ref={canvasRef}
          width={420}
          height={130}
          className="w-full cursor-crosshair touch-none"
          style={{ height: 130 }}
        />
        <p className="absolute bottom-2 left-0 right-0 text-center text-[10px] text-muted-foreground pointer-events-none select-none">
          Sign above
        </p>
      </div>
      <button
        type="button"
        onClick={clear}
        className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
      >
        <Trash2 className="h-3 w-3" />
        Clear
      </button>
    </div>
  );
}

// ── Quiz View ─────────────────────────────────────────────────────────────────

type QuizStage = "idle" | "loading" | "active" | "result";

function QuizSection({ assignmentId, docStatus, onPassed, onLocked, assignerName, docViewed = true }: {
  assignmentId: string;
  docStatus: string;
  onPassed: () => void;
  onLocked: () => void;
  assignerName?: string;
  docViewed?: boolean;
}) {
  const [stage, setStage] = useState<QuizStage>("idle");
  const [quizData, setQuizData] = useState<QuizData | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<{ passed: boolean; score: number; correct: number; total: number; passScore: number; attemptNumber: number; locked?: boolean } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const alreadyPassed = docStatus === "quiz_passed";
  const isLocked = docStatus === "quiz_locked";

  async function startQuiz() {
    setStage("loading");
    try {
      const data = await apiGet<QuizData>(`/api/doc-assignments/${assignmentId}/quiz`);
      setQuizData(data);
      setAnswers({});
      setResult(null);
      setStage("active");
    } catch (e: any) {
      toast.error(e.message || "Failed to load quiz");
      setStage("idle");
    }
  }

  async function submitAnswers() {
    if (!quizData) return;
    const unanswered = quizData.questions.filter(q => !answers[q.id]);
    if (unanswered.length > 0) {
      toast.error(`Please answer all ${quizData.questions.length} questions`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiPost<{ passed: boolean; score: number; correct: number; total: number; passScore: number; attemptNumber: number; locked?: boolean }>(
        `/api/doc-assignments/${assignmentId}/quiz/attempt`,
        { answers }
      );
      setResult(res);
      setStage("result");
      if (res.passed) onPassed();
      else if (res.locked) onLocked();
    } catch (e: any) {
      toast.error(e.message || "Failed to submit quiz");
    } finally {
      setSubmitting(false);
    }
  }

  if (alreadyPassed) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
        <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">Quiz Passed</p>
          <p className="text-xs text-emerald-600/80 dark:text-emerald-500/80">You can now sign the document below.</p>
        </div>
      </div>
    );
  }

  if (isLocked) {
    return (
      <div className="flex items-start gap-2 p-4 rounded-lg bg-amber-500/10 border border-amber-500/40">
        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">Quiz Locked — 3 Failed Attempts</p>
          <p className="text-xs text-muted-foreground">
            {assignerName
              ? <>Ask <span className="font-medium text-foreground">{assignerName}</span> to unlock your quiz.</>
              : "Contact your admin to unlock your quiz."}
          </p>
        </div>
      </div>
    );
  }

  if (stage === "idle") {
    return (
      <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/5 space-y-3">
        <div className="flex items-start gap-2">
          <BookOpen className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Quiz Required</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              You must pass the quiz before signing this document.
            </p>
            {!docViewed && (
              <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mt-1">
                Open and read the document first. The quiz unlocks once you have viewed it.
              </p>
            )}
          </div>
        </div>
        <Button size="sm" onClick={startQuiz} disabled={!docViewed} className="w-full">
          Begin Quiz
        </Button>
      </div>
    );
  }

  if (stage === "loading") {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (stage === "result" && result) {
    return (
      <div className="space-y-3">
        <div className={`p-4 rounded-lg border ${result.passed ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"}`}>
          <div className="flex items-center gap-2 mb-2">
            {result.passed
              ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              : <XCircle className="h-5 w-5 text-red-600" />
            }
            <p className={`text-sm font-semibold ${result.passed ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}`}>
              {result.passed ? "Quiz Passed!" : "Quiz Failed"}
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            Score: <span className="font-semibold text-foreground">{result.score}%</span>
            {" "}({result.correct}/{result.total} correct) · Pass threshold: {result.passScore}%
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Attempt #{result.attemptNumber}
          </p>
        </div>
        {!result.passed && !result.locked && (
          <Button variant="outline" size="sm" onClick={startQuiz} className="w-full gap-2">
            <RotateCcw className="h-4 w-4" />
            Try Again
          </Button>
        )}
        {!result.passed && result.locked && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/40">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                Quiz locked after 3 failed attempts.
              </p>
              <p className="text-xs text-muted-foreground">
                {assignerName
                  ? <>Ask <span className="font-medium text-foreground">{assignerName}</span> to unlock in{" "}</>
                  : "Your admin can unlock in "}
                <a href="/admin/assignments" className="font-medium text-amber-600 dark:text-amber-400 underline underline-offset-2">
                  Admin → Assignments
                </a>.
              </p>
            </div>
          </div>
        )}
        {result.passed && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
            <p className="text-xs text-emerald-700 dark:text-emerald-400">You can now sign the document below.</p>
          </div>
        )}
      </div>
    );
  }

  // Active quiz
  if (stage === "active" && quizData) {
    const answered = Object.keys(answers).length;
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">{quizData.questions.length} Questions · Pass {quizData.passScore}%</p>
          <p className="text-xs text-muted-foreground">{answered}/{quizData.questions.length} answered</p>
        </div>

        <div className="space-y-5 max-h-[50vh] overflow-y-auto pr-1">
          {quizData.questions.map((q, idx) => {
            const opts: [string, string | null][] = [
              ["a", q.optionA],
              ["b", q.optionB],
              ["c", q.optionC],
              ["d", q.optionD],
            ];
            return (
              <div key={q.id} className="space-y-2">
                <p className="text-sm font-medium leading-snug">
                  <span className="text-muted-foreground mr-1">{idx + 1}.</span>
                  {q.questionText}
                </p>
                <div className="space-y-1.5 pl-1" role="radiogroup">
                  {opts.filter(([, label]) => label !== null).map(([key, label]) => {
                    const selected = answers[q.id] === key;
                    return (
                      <div
                        key={key}
                        role="radio"
                        aria-checked={selected}
                        tabIndex={0}
                        onClick={() => setAnswers(prev => ({ ...prev, [q.id]: key }))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setAnswers(prev => ({ ...prev, [q.id]: key }));
                          }
                        }}
                        className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                          selected
                            ? "border-blue-500 bg-blue-500/10"
                            : "border-border hover:bg-muted/50"
                        }`}
                      >
                        <div className={`mt-0.5 h-4 w-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                          selected ? "border-blue-500 bg-blue-500" : "border-muted-foreground"
                        }`}>
                          {selected && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                        </div>
                        <span className="text-sm leading-snug">{label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <Button
          onClick={submitAnswers}
          disabled={submitting || answered < quizData.questions.length}
          className="w-full gap-2"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          Submit Answers
        </Button>
      </div>
    );
  }

  return null;
}

// ── Assignment Sheet ──────────────────────────────────────────────────────────

function AckSheet({ ack, onClose, onComplete }: {
  ack: MyAssignment;
  onClose: () => void;
  onComplete: () => void;
}) {
  const qc = useQueryClient();
  const [downloading, setDownloading] = useState(false);
  const [docStatus, setDocStatus] = useState(ack.docStatus);
  const [signaturePng, setSignaturePng] = useState<string | null>(null);
  const [fullName, setFullName] = useState("");
  const [signing, setSigning] = useState(false);
  // The quiz stays locked until the worker has opened the document.
  const [docViewed, setDocViewed] = useState(false);

  // Check if document has a quiz via the assignment quiz endpoint
  const { data: quizInfo, isLoading: quizLoading } = useQuery({
    queryKey: ["assignment-quiz-info", ack.assignmentId],
    queryFn: () => apiGet<QuizData>(`/api/doc-assignments/${ack.assignmentId}/quiz`).catch(e => {
      if (e.message?.includes("no quiz") || e.message?.includes("has no quiz")) return null;
      throw e;
    }),
    retry: false,
    enabled: ack.completionStatus !== "approved",
  });

  const hasQuiz = !!(quizInfo && quizInfo.questions?.length > 0);
  const quizPassed = docStatus === "quiz_passed";
  const canSign = !hasQuiz || quizPassed;
  const isCompleted = ack.completionStatus === "approved";

  async function handleViewDoc() {
    if (!ack.doc?.id) return;
    // Fetch doc to get currentVersionId
    setDownloading(true);
    try {
      const docData = await apiGet<{ data: { doc: { currentVersionId: string | null } } }>(`/api/admin/safety-docs/${ack.doc.id}`).catch(() => null);
      const versionId = (docData as any)?.currentVersionId || (docData as any)?.doc?.currentVersionId;
      if (!versionId) { toast.error("No document file available"); return; }

      const { url, fileName } = await apiGet<{ url: string; fileName: string; expiresIn: number }>(
        `/api/admin/safety-docs/${ack.doc.id}/versions/${versionId}/url`
      );
      const isPresigned = url.includes("X-Amz-Signature") || url.includes("x-amz-signature");
      const a = document.createElement("a");
      if (isPresigned) {
        a.href = url; a.target = "_blank"; a.click();
      } else {
        const BASE = import.meta.env.VITE_API_URL ?? "";
        const fullUrl = url.startsWith("http") ? url : `${BASE}${url}`;
        const res = await fetch(fullUrl, { credentials: "include" });
        if (!res.ok) throw new Error("Failed to fetch file");
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        a.href = blobUrl; a.target = "_blank"; a.click();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      }
      setDocViewed(true);
    } catch (e: any) {
      toast.error(e.message || "Failed to open document");
    } finally {
      setDownloading(false);
    }
  }

  const { data: nameSession } = authClient.useSession();
  const norm = (v: string) => v.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, " ").trim().toLowerCase();
  const registeredName = nameSession?.user?.name ?? "";
  const nameMatches = !registeredName || norm(fullName) === norm(registeredName);

  async function handleSign() {
    if (!nameMatches) { toast.error(`Type your full name exactly as registered: ${registeredName}`); return; }
    if (!signaturePng) { toast.error("Please draw your signature"); return; }
    if (!fullName.trim()) { toast.error("Please enter your full name"); return; }
    setSigning(true);
    try {
      await apiPost(`/api/doc-assignments/${ack.assignmentId}/sign`, {
        signatureData: signaturePng,
        signedFullName: fullName.trim(),
      });
      toast.success("Document signed and completed!");
      onComplete();
      onClose();
    } catch (e: any) {
      toast.error(e.message || "Failed to sign document");
    } finally {
      setSigning(false);
    }
  }

  return (
    <Sheet open onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto flex flex-col gap-0 p-0">
        <SheetHeader className="px-5 pt-5 pb-4 border-b border-border">
          <SheetTitle className="text-base leading-snug">{ack.doc?.title ?? "Document"}</SheetTitle>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            {ack.assigner && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <User className="h-3 w-3" />
                Assigned by {ack.assigner.name}
              </span>
            )}
            {ack.dueDate && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3" />
                Due {formatDateOnly(ack.dueDate)}
              </span>
            )}
          </div>
          {ack.message && (
            <MessageText text={ack.message} className="text-xs text-muted-foreground mt-1" />
          )}
        </SheetHeader>

        <div className="flex-1 px-5 py-5 space-y-5">
          {/* View document */}
          <div>
            <Button variant="outline" size="sm" onClick={handleViewDoc} disabled={downloading} className="gap-2">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
              View Document
            </Button>
          </div>

          {isCompleted ? (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <CheckCircle2 className="h-7 w-7 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Completed</p>
                <p className="text-xs text-muted-foreground">
                  Signed {ack.signedAt ? new Date(ack.signedAt).toLocaleDateString() : ""}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Quiz section */}
              {quizLoading ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking quiz requirements…
                </div>
              ) : hasQuiz ? (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">Step 1 — Pass the Quiz</h3>
                  <QuizSection
                    assignmentId={ack.assignmentId}
                    docStatus={docStatus}
                    assignerName={ack.assigner?.name}
                    docViewed={docViewed}
                    onPassed={() => setDocStatus("quiz_passed")}
                    onLocked={() => {
                      setDocStatus("quiz_locked");
                      qc.invalidateQueries({ queryKey: ["my-doc-assignments"] });
                    }}
                  />
                </div>
              ) : null}

              {/* Signing section */}
              <div className={`space-y-3 ${hasQuiz && !quizPassed ? "opacity-40 pointer-events-none select-none" : ""}`}>
                <h3 className="text-sm font-semibold">{hasQuiz ? "Step 2 — Sign the Document" : "Sign the Document"}</h3>

                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Full Name</Label>
                    <Input
                      value={fullName}
                      onChange={e => setFullName(e.target.value)}
                      placeholder="Your full legal name"
                      className="h-9"
                    />
                    {fullName.trim() && !nameMatches && (
                      <p className="text-[11px] text-destructive">Must match your registered name: {registeredName}</p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Signature</Label>
                    <SignaturePad
                      onCapture={setSignaturePng}
                      onClear={() => setSignaturePng(null)}
                    />
                  </div>

                  <Button
                    onClick={handleSign}
                    disabled={signing || !canSign || !signaturePng || !fullName.trim() || !nameMatches}
                    className="w-full gap-2"
                  >
                    {signing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
                    Sign &amp; Complete
                  </Button>

                  <p className="text-[10px] text-muted-foreground text-center">
                    By signing, you confirm you have read and understood this document.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── My Assignments View ───────────────────────────────────────────────────────

export function MyAssignmentsView() {
  const qc = useQueryClient();
  const [selectedAck, setSelectedAck] = useState<MyAssignment | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["my-doc-assignments"],
    queryFn: () => apiGet<{ data: MyAssignment[] }>("/api/doc-assignments/mine"),
  });
  const all = data?.data ?? [];
  const pending = all.filter(a => a.completionStatus !== "approved");
  const completed = all.filter(a => a.completionStatus === "approved");

  function handleComplete() {
    qc.invalidateQueries({ queryKey: ["my-doc-assignments"] });
  }

  if (isLoading) {
    return (
      <div className="space-y-2 py-4">
        {[1, 2, 3].map(i => <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />)}
      </div>
    );
  }

  if (all.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center py-14 text-center">
          <ClipboardList className="h-10 w-10 text-muted-foreground/30 mb-3" />
          <p className="font-medium text-muted-foreground text-sm">No assignments</p>
          <p className="text-xs text-muted-foreground/70 mt-1">You have no document assignments yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {pending.length === 0 && !showCompleted ? (
        <Card>
          <CardContent className="flex flex-col items-center py-10 text-center">
            <CheckCircle2 className="h-9 w-9 text-emerald-500/60 mb-3" />
            <p className="font-medium text-sm">All caught up!</p>
            <p className="text-xs text-muted-foreground mt-1">You have no pending assignments.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {pending.map(ack => (
            <AssignmentCard key={ack.ackId} ack={ack} onClick={() => setSelectedAck(ack)} />
          ))}
        </div>
      )}

      {completed.length > 0 && (
        <div className="space-y-2">
          <button
            onClick={() => setShowCompleted(v => !v)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {showCompleted ? <ChevronLeft className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {showCompleted ? "Hide" : "Show"} {completed.length} completed
          </button>
          {showCompleted && (
            <div className="space-y-2 opacity-70">
              {completed.map(ack => (
                <AssignmentCard key={ack.ackId} ack={ack} onClick={() => setSelectedAck(ack)} />
              ))}
            </div>
          )}
        </div>
      )}

      {selectedAck && (
        <AckSheet
          ack={selectedAck}
          onClose={() => setSelectedAck(null)}
          onComplete={handleComplete}
        />
      )}
    </div>
  );
}

function AssignmentCard({ ack, onClick }: { ack: MyAssignment; onClick: () => void }) {
  const isOverdue = ack.dueDate && ack.completionStatus !== "approved" && parseDateOnly(ack.dueDate) < new Date();
  return (
    <Card
      className={`p-0 cursor-pointer hover:shadow-sm transition-shadow ${isOverdue ? "border-red-400/40" : ""}`}
      onClick={onClick}
    >
      <CardContent className="flex items-center gap-3 px-4 py-3">
        <FileText className={`h-8 w-8 flex-shrink-0 ${ack.completionStatus === "approved" ? "text-emerald-500/60" : "text-paris-red-text/50"}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-sm truncate">{ack.doc?.title ?? "Document"}</p>
            <StatusBadge status={ack.completionStatus} />
            {isOverdue && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-red-500/15 text-red-700 dark:text-red-400">
                <AlertTriangle className="h-3 w-3" />
                Overdue
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {ack.assigner?.name ? `From ${ack.assigner.name}` : ""}
            {ack.dueDate ? ` · Due ${formatDateOnly(ack.dueDate)}` : ""}
          </p>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      </CardContent>
    </Card>
  );
}

// ── Library View (existing) ───────────────────────────────────────────────────

function LibraryView() {
  const [selectedCatId, setSelectedCatId] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const navigate = useNavigate();
  /** Opens a folder, or its own page when the folder has one (Toolbox Meetings -> the toolbox library). */
  const openFolder = (cat: DocCategory) => {
    const page = folderPage(cat.name);
    if (page) {
      navigate({ to: page as "/toolbox/library", search: {} });
      return;
    }
    setSelectedCatId(cat.id);
  };

  const phoneTopRef = useRef<HTMLDivElement>(null);
  /** Phone explorer: go into a folder, or up (null = the folder list), and show it from the top. */
  const goTo = (target: DocCategory | string | null) => {
    const cat = typeof target === "string" ? cats.find((c) => c.id === target) : target;
    if (cat) openFolder(cat);
    else setSelectedCatId(null);
    window.setTimeout(() => phoneTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const { data: catsData } = useQuery({
    queryKey: ["safety-doc-categories"],
    queryFn: () => apiGet<{ data: DocCategory[] }>("/api/admin/safety-docs/categories"),
  });
  const cats = catsData?.data ?? [];

  // All visible documents, filtered below by the selected folder (and its subfolders).
  const { data: docsData, isLoading } = useQuery({
    queryKey: ["safety-docs", "all-active"],
    queryFn: () =>
      apiGet<{ data: { doc: SafetyDoc; category: { name: string } | null }[] }>(
        `/api/admin/safety-docs?status=active`
      ),
  });

  // Folder tree: top-level folders, with subfolders nested under their parent.
  const childrenOf = (id: string) => cats.filter((c) => c.parentId === id);
  const descendantIds = (id: string): string[] =>
    childrenOf(id).flatMap((c) => [c.id, ...descendantIds(c.id)]);
  const allDocs = docsData?.data ?? [];
  // Like a file explorer: a folder lists only the documents directly inside it;
  // documents in a subfolder show once you open that subfolder.
  const docs = selectedCatId ? allDocs.filter(({ doc }) => doc.categoryId === selectedCatId) : allDocs;
  const selectedFolder = cats.find((c) => c.id === selectedCatId) ?? null;
  const parentOf = (id: string) => cats.find((c) => c.id === id)?.parentId ?? null;
  /** Folder names from the top down to the given folder. */
  function pathTo(id: string): DocCategory[] {
    const out: DocCategory[] = [];
    let cur: string | null = id;
    while (cur) {
      const c = cats.find((x) => x.id === cur);
      if (!c) break;
      out.unshift(c);
      cur = c.parentId ?? null;
    }
    return out;
  }
  const directDocCount = (id: string) => allDocs.filter(({ doc }) => doc.categoryId === id).length;

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  // A folder is open when it, or anything inside it, is selected.
  const isOpenFolder = (id: string) =>
    selectedCatId === id || (selectedCatId ? descendantIds(id).includes(selectedCatId) : false);

  const topCompany = cats.filter((c) => c.scope === "company" && !c.parentId);
  const topDivision = cats.filter((c) => c.scope === "division" && !c.parentId);

  /** Desktop sidebar tree: folders with expandable subfolders. */
  function renderTree(list: DocCategory[], depth = 0) {
    return list.map((cat) => {
      const kids = childrenOf(cat.id);
      const isExpanded = expanded.has(cat.id) || isOpenFolder(cat.id);
      const selected = selectedCatId === cat.id;
      return (
        <div key={cat.id}>
          <div className="flex items-center" style={{ paddingLeft: depth * 14 }}>
            {kids.length > 0 ? (
              <button
                type="button"
                aria-label={isExpanded ? "Collapse folder" : "Expand folder"}
                onClick={() => toggleExpanded(cat.id)}
                className="p-1 text-muted-foreground hover:text-foreground"
              >
                <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
              </button>
            ) : (
              <span className="w-[22px]" />
            )}
            <button
              onClick={() => { openFolder(cat); if (kids.length > 0) setExpanded((p) => new Set(p).add(cat.id)); }}
              className={`flex-1 min-w-0 flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-left transition-colors ${
                selected ? "bg-paris-red/10 text-paris-red-text font-medium" : "hover:bg-muted text-muted-foreground"
              }`}
            >
              {selected ? <FolderOpen className="h-4 w-4 flex-shrink-0" /> : <Folder className="h-4 w-4 flex-shrink-0" />}
              <span className="truncate">{cat.name}</span>
            </button>
          </div>
          {kids.length > 0 && isExpanded && renderTree(kids, depth + 1)}
        </div>
      );
    });
  }

  async function fetchAndOpen(docId: string, versionId: string, mode: "view" | "download") {
    setDownloading(versionId);
    try {
      await openDocFile(docId, versionId, mode);
    } catch (e: any) {
      toast.error(e.message || "Failed to open file");
    } finally {
      setDownloading(null);
    }
  }

  /** Subfolders (as folder rows) followed by the documents directly in the open folder. */
  function renderFolderContents(emptyHint: string, phone = false) {
    const kids = selectedCatId ? childrenOf(selectedCatId) : [];
    const parentId = selectedCatId ? parentOf(selectedCatId) : null;
    return (
      <div className="space-y-2">
        {!phone && selectedFolder && parentId && (
          <button
            type="button"
            onClick={() => setSelectedCatId(parentId)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <ChevronRight className="h-3.5 w-3.5 rotate-180" />
            Back to {cats.find((c) => c.id === parentId)?.name}
          </button>
        )}
        {kids.length > 0 && (
          <div className="rounded-lg border border-border overflow-hidden">
            {kids.map((k, idx) => {
              const subCount = childrenOf(k.id).length;
              const n = directDocCount(k.id);
              return (
                <button
                  key={k.id}
                  type="button"
                  onClick={() => { openFolder(k); setExpanded((p) => new Set(p).add(k.id)); if (phone) window.setTimeout(() => phoneTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left bg-card hover:bg-muted/30 transition-colors ${idx !== kids.length - 1 ? "border-b border-border" : ""}`}
                >
                  <Folder className="h-5 w-5 text-paris-red-text/70 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{k.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {n} document{n !== 1 ? "s" : ""}{subCount > 0 ? ` · ${subCount} subfolder${subCount !== 1 ? "s" : ""}` : ""}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              );
            })}
          </div>
        )}
        {/* A folder that only holds subfolders needs no "empty" message */}
        {(docs.length > 0 || isLoading || kids.length === 0) && renderDocList(emptyHint)}
      </div>
    );
  }

  function renderDocList(emptyHint: string) {
    if (isLoading) {
      return (
        <div className="rounded-lg border border-border overflow-hidden">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-14 bg-muted/40 animate-pulse border-b border-border last:border-0" />
          ))}
        </div>
      );
    }
    if (docs.length === 0) {
      return (
        <div className="rounded-lg border border-border flex flex-col items-center py-10 text-center bg-card">
          <FileWarning className="h-8 w-8 text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">{emptyHint}</p>
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-border overflow-hidden">
        {docs.map(({ doc, category }, idx) => (
          <div
            key={doc.id}
            className={`flex items-center gap-3 px-4 py-3 ${idx !== docs.length - 1 ? "border-b border-border" : ""} bg-card hover:bg-muted/30 transition-colors`}
          >
            <FileText className="h-5 w-5 text-paris-red-text/60 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium text-sm break-words">{doc.title}</p>
                <VisibilityBadge visibility={doc.visibility} />
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {category?.name ?? "—"} · Updated {new Date(doc.updatedAt).toLocaleDateString()}
              </p>
            </div>
            {doc.currentVersionId && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  title="View"
                  disabled={downloading === doc.currentVersionId}
                  onClick={() => fetchAndOpen(doc.id, doc.currentVersionId!, "view")}
                >
                  {downloading === doc.currentVersionId
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Eye className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  title="Download"
                  disabled={downloading === doc.currentVersionId}
                  onClick={() => fetchAndOpen(doc.id, doc.currentVersionId!, "download")}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {/* ── Phone: a file explorer. A folder opens its own page (subfolders, then documents) with a way back ── */}
      <div ref={phoneTopRef} className="md:hidden space-y-2 scroll-mt-16">
        {selectedFolder ? (
          <>
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label="Back"
                onClick={() => goTo(parentOf(selectedFolder.id))}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border text-muted-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <nav className="flex min-w-0 flex-1 flex-wrap items-center text-sm" aria-label="Folder path">
                <button type="button" className="py-1 text-muted-foreground" onClick={() => goTo(null)}>Folders</button>
                {pathTo(selectedFolder.id).map((c, i, arr) => (
                  <span key={c.id} className="flex items-center">
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                    <button type="button" className={`py-1 ${i === arr.length - 1 ? "font-semibold" : "text-muted-foreground"}`} onClick={() => goTo(c.id)}>{c.name}</button>
                  </span>
                ))}
              </nav>
            </div>
            {renderFolderContents("No documents in this folder yet.", true)}
          </>
        ) : cats.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No folders yet.</p>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            {[...topCompany, ...topDivision].map((cat, idx, all) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => goTo(cat)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left bg-card hover:bg-muted/30 transition-colors ${idx !== all.length - 1 ? "border-b border-border" : ""}`}
              >
                <Folder className="h-5 w-5 text-paris-red-text/70 shrink-0" />
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{cat.name}</span>
                {cat.scope === "company" && (
                  <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">Company</span>
                )}
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Desktop: sidebar + list ──────────────────────────────────────── */}
      <div className="hidden md:flex gap-5 min-h-[60vh]">
        {/* Left: Category tree */}
        <div className="w-56 flex-shrink-0 space-y-0.5">
          <button
            onClick={() => setSelectedCatId(null)}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
              selectedCatId === null
                ? "bg-paris-red/10 text-paris-red-text font-medium"
                : "hover:bg-muted text-muted-foreground"
            }`}
          >
            <FolderOpen className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">All Documents</span>
          </button>

          {topCompany.length > 0 && (
            <div className="pt-1">
              <p className="px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                Company
              </p>
              {renderTree(topCompany)}
            </div>
          )}

          {topDivision.length > 0 && (
            <div className="pt-1">
              <p className="px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                Division
              </p>
              {renderTree(topDivision)}
            </div>
          )}

          {cats.length === 0 && (
            <p className="px-3 text-xs text-muted-foreground py-3">No folders yet.</p>
          )}
        </div>

        {/* Right: Document list */}
        <div className="flex-1 min-w-0 space-y-2">
          {selectedFolder && (
            <div className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground mb-1">
              <button type="button" onClick={() => setSelectedCatId(null)} className="hover:text-foreground">All Documents</button>
              {pathTo(selectedFolder.id).map((c, i, arr) => (
                <span key={c.id} className="flex items-center gap-1">
                  <ChevronRight className="h-3.5 w-3.5" />
                  {i === arr.length - 1 ? (
                    <span className="font-medium text-foreground">{c.name}</span>
                  ) : (
                    <button type="button" onClick={() => setSelectedCatId(c.id)} className="hover:text-foreground">{c.name}</button>
                  )}
                </span>
              ))}
            </div>
          )}
          {selectedCatId ? renderFolderContents("No documents in this folder yet.") : renderDocList("No documents yet.")}
        </div>
      </div>
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

function DocumentsPage() {
  const { tab: tabParam } = Route.useSearch();
  const [tab, setTab] = useState<"assignments" | "library">(tabParam ?? "library");
  // A link to this page can ask for a specific tab (e.g. from a notice).
  useEffect(() => { if (tabParam) setTab(tabParam); }, [tabParam]);

  return (
    <div className="max-w-5xl mx-auto space-y-4 pb-10">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Safety Documents</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Company safety documents, procedures, and programs.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit">
        <button
          onClick={() => setTab("assignments")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            tab === "assignments"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <ClipboardList className="h-4 w-4" />
          My Assignments
        </button>
        <button
          onClick={() => setTab("library")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            tab === "library"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <FolderOpen className="h-4 w-4" />
          Library
        </button>
      </div>

      {tab === "assignments" ? <MyAssignmentsView /> : <LibraryView />}
    </div>
  );
}
